<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import AdminNavigation from '@/components/admin/AdminNavigation.vue'
import CreateBridgeCustomerForm from './CreateBridgeCustomerForm.vue'
import CreateTransferForm from './CreateTransferForm.vue'
import BridgeCombinedList from './BridgeCombinedList.vue'
import BridgeTransfersList from './BridgeTransfersList.vue'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { API, HOST } from '@/utils/http'
import type { BridgeCustomer, BridgeOrganization, CombinedCustomer, BridgeTransfer } from '@/types/bridge'

const store = useStore()
const member = computed(() => store.getters.getMember)

// Active tab
const activeTab = ref<'customers' | 'transfers'>('customers')

// Customers state
const customers = ref<BridgeCustomer[]>([])
const customersLoading = ref(false)
const customersError = ref<string | null>(null)

// Organizations state
const organizations = ref<BridgeOrganization[]>([])
const organizationsLoading = ref(false)
const organizationsError = ref<string | null>(null)

// Transfers state
const transfers = ref<BridgeTransfer[]>([])
const transfersLoading = ref(false)
const transfersError = ref<string | null>(null)

// Combined loading state
const isLoading = computed(() => customersLoading.value || organizationsLoading.value)

// Combined error
const error = computed(() => customersError.value || organizationsError.value || transfersError.value)

// Combined customers list
const combinedCustomers = computed<CombinedCustomer[]>(() => {
  const individuals = customers.value.map(c => ({
    type: 'individual' as const,
    id: c.id,
    bridge_customer_id: c.bridge_customer_id,
    name: c.member_email || 'Unknown',
    kyc_status: c.kyc_status,
    tos_status: c.tos_status,
    created_at: c.created_at,
    link: c.link
  }))

  const businesses = organizations.value.map(o => ({
    type: 'business' as const,
    id: o.id,
    bridge_customer_id: o.bridge_customer_id,
    name: o.organization_name || `Org #${o.organization_id}`,
    contactName: o.contact_first_name && o.contact_last_name
      ? `${o.contact_first_name} ${o.contact_last_name}`
      : undefined,
    contactEmail: o.contact_email || undefined,
    kyc_status: o.kyc_status,
    tos_status: o.tos_status,
    created_at: o.created_at,
    link: o.link
  }))

  return [...individuals, ...businesses]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
})

const fetchCustomers = async () => {
  customersLoading.value = true
  customersError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/payments/bridge/customers`
    })
    customers.value = response.data
  } catch (err: any) {
    console.error('Error fetching Bridge customers:', err)
    customersError.value = err.response?.data?.detail || 'Failed to fetch customers'
  } finally {
    customersLoading.value = false
  }
}

const fetchOrganizations = async () => {
  organizationsLoading.value = true
  organizationsError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/payments/bridge/organizations`
    })
    organizations.value = response.data
  } catch (err: any) {
    console.error('Error fetching Bridge organizations:', err)
    organizationsError.value = err.response?.data?.detail || 'Failed to fetch organizations'
  } finally {
    organizationsLoading.value = false
  }
}

const fetchTransfers = async () => {
  transfersLoading.value = true
  transfersError.value = null

  try {
    const response = await API({
      method: 'GET',
      url: `${HOST}/payments/bridge/transfers`,
      params: { limit: 100 }
    })
    transfers.value = response.data
  } catch (err: any) {
    console.error('Error fetching Bridge transfers:', err)
    transfersError.value = err.response?.data?.detail || 'Failed to fetch transfers'
  } finally {
    transfersLoading.value = false
  }
}

const handleCustomerCreated = () => {
  fetchCustomers()
  fetchOrganizations()
}

const handleTransferCreated = () => {
  fetchTransfers()
}

onMounted(() => {
  fetchCustomers()
  fetchOrganizations()
  fetchTransfers()
})
</script>

<template>
  <div v-if="member && member.is_super_user" class="h-full flex flex-col">
    <AdminNavigation />

    <div class="p-6 space-y-4 flex-1 overflow-auto">
      <!-- Error display -->
      <div v-if="error" class="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
        {{ error }}
      </div>

      <!-- Tabs for Customers and Transfers -->
      <Tabs v-model="activeTab" class="w-full">
        <div class="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="customers">Customers</TabsTrigger>
            <TabsTrigger value="transfers">Transfers</TabsTrigger>
          </TabsList>
          <CreateBridgeCustomerForm v-if="activeTab === 'customers'" @customer-created="handleCustomerCreated" />
          <CreateTransferForm v-if="activeTab === 'transfers'" @transfer-created="handleTransferCreated" />
        </div>

        <!-- Customers Tab -->
        <TabsContent value="customers" class="mt-4">
          <div class="text-sm text-gray-600 mb-4">
            Total customers: {{ combinedCustomers.length }}
          </div>
          <BridgeCombinedList
            :customers="combinedCustomers"
            :loading="isLoading"
            @customer-updated="handleCustomerCreated"
          />
        </TabsContent>

        <!-- Transfers Tab -->
        <TabsContent value="transfers" class="mt-4">
          <div class="text-sm text-gray-600 mb-4">
            Total transfers: {{ transfers.length }}
          </div>
          <BridgeTransfersList
            :transfers="transfers"
            :loading="transfersLoading"
          />
        </TabsContent>
      </Tabs>
    </div>
  </div>
</template>
